// components/Login.tsx
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Avatar,
  alpha,
} from "@mui/material";
import { LockOutlined as LockIcon } from "@mui/icons-material";
import { supabase } from "../lib/supabase";
import { gradients, shadows } from "../theme/theme";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleLocalDemoLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/auth/demo-login`, {
        method: "POST",
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.detail || "Demo login failed");
      }

      const { data, error } = await supabase.auth.setSession({
        access_token: payload.access_token,
        refresh_token: payload.refresh_token,
      });

      if (error || !data.user) {
        throw new Error(error?.message || "Demo session was not created");
      }

    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Demo login failed";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Card
          sx={{
            maxWidth: 440,
            mx: "auto",
            overflow: "hidden",
            border: `1px solid ${alpha("#6366F1", 0.2)}`,
            boxShadow: shadows.float,
          }}
        >
          {/* Gradient accent bar */}
          <Box sx={{ height: 3, background: gradients.primary }} />

          <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  m: 1,
                  width: 48,
                  height: 48,
                  bgcolor: alpha("#6366F1", 0.15),
                  color: "#818CF8",
                }}
              >
                <LockIcon />
              </Avatar>
              <Typography component="h1" variant="h5">
                Sign in to LocateNow Pro
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#F1F5F9",
                },
              }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
              {isLocalhost && (
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  onClick={handleLocalDemoLogin}
                  sx={{ py: 1.25 }}
                >
                  {loading ? "Opening demo..." : "Use Local Demo"}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
